import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners, pointerWithin, defaultDropAnimation } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { FaPlaneDeparture, FaPlaneArrival, FaGripVertical, FaGlobeAmericas, FaCheckCircle, FaExchangeAlt, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ColunaArrastavel, { alturaCelula, alturaAtiva, OverlayConteudo } from './ColunaArrastavel';
import { decompor, fundir, separarLado, mesclarLado, resumoTrecho, remapLado } from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BLUE   = '#2d7ff9';
const BRAND_BORDER = '#dbe8f8';

// Opção que está no evento: anda como uma linha só, como o par internacional, e o Agrupar e o Excluir
// dela avisam em vez de agir. A mesma regra do X do chip e da exclusão na planilha.
const AVISO_IMPORTADA = 'Esta opção está no evento: não se agrupa nem se exclui aqui. O RSVP a retira na Importação das Opções de Aéreo antes.';

// Badge do item unido: o globo do internacional ou o "No evento" da importada, no mesmo desenho.
const BADGE_ITEM = { fontSize: 10, letterSpacing: '0.04em', borderRadius: 6, flexShrink: 0 };

// Cada trecho ganha um _uid estável → é o id do sub-chip arrastável dentro do
// grupo (separarLado só dá _id ao 1º; os demais precisam de identidade própria
// p/ o dnd-kit reordenar/extrair sem teleporte).
const comUid = (itens, prefixo) => itens.map((t, j) => ({ ...t, _uid: t._uid ?? `u-${prefixo}-${j}` }));

// Célula vazia (placeholder) com id único.
const novoVazio = (lado) => ({ id: `${lado}-z${Math.random().toString(36).slice(2, 9)}`, itens: [] });

// Põe `item` na posição `i` da coluna, empurrando o resto para baixo. Nas duas colunas ao mesmo
// tempo, é uma linha nova ali.
const inserir = (lista, i, item) => [...lista.slice(0, i), item, ...lista.slice(i)];

// Invariante das colunas: linhas com conteúdo (em ordem) + EXATAMENTE UM par de
// whitespace no fim. Remove pares totalmente vazios (limpa após mover) e garante sempre
// um slot vazio p/ anexar. Mantém alinhamento IDA × RET por índice.
const normalizarPares = (ida, ret) => {
    const n = Math.max(ida.length, ret.length);
    const ci = [], cr = [];
    for (let i = 0; i < n; i++) {
        const a = ida[i] || novoVazio('ida');
        const b = ret[i] || novoVazio('ret');
        if (a.itens.length > 0 || b.itens.length > 0) { ci.push(a); cr.push(b); }
    }
    ci.push(novoVazio('ida'));
    cr.push(novoVazio('ret'));
    return [ci, cr];
};

// Já satisfaz a invariante? (evita re-normalizar/loop no useEffect.)
const jaNormalizado = (ida, ret) => {
    const n = Math.max(ida.length, ret.length);
    if (n === 0) return false;
    for (let i = 0; i < n; i++) {
        const vazia = (!ida[i] || ida[i].itens.length === 0) && (!ret[i] || ret[i].itens.length === 0);
        if (i < n - 1 && vazia) return false;   // par vazio no meio
        if (i === n - 1 && !vazia) return false; // última linha não é o par vazio
    }
    return true;
};

/**
 * O par internacional desenhado como UM item: ocupa as duas colunas (200% da coluna mais o vão
 * `gap-2` entre elas) a partir da metade da IDA, com a do RETORNO cedendo o espaço. As duas
 * metades têm a mesma largura, então o divisor cai no vão: a ida fica sob o cabeçalho IDA e a
 * volta sob o RETORNO. O card é a própria caixa de largura fixa — dentro de um flex ele
 * encolheria até o conteúdo. A opção que está no evento usa o mesmo item; o que muda é o badge
 * (`inter`, `importada`).
 */
function ParTravado({ ida, ret, resumoIda, resumoRet, overlay, acoes, inter = true, importada = false }) {
    const lista = (itens, resumo) => (
        <div className="d-flex flex-column" style={{ flex: '1 1 0', minWidth: 0 }}>
            {itens.length === 0
                ? <span style={{ fontSize: 12, color: '#b9c4d6' }}>—</span>
                : itens.map((t, i) => {
                    const texto = resumo(t);
                    return (
                        <span key={t._uid ?? t._id ?? i} className="text-truncate"
                            style={{ minWidth: 0, fontSize: 11.5, lineHeight: '16px' }}
                            title={typeof texto === 'string' ? texto : undefined}>{texto}</span>
                    );
                })}
        </div>
    );
    return (
        <div className="d-flex align-items-stretch"
            style={{
                width: 'calc(200% + 0.5rem)', minHeight: 40, background: '#eef6ff',
                border: `1px solid ${BRAND_BORDER}`, borderRadius: 10, color: BRAND_NAVY,
                boxShadow: overlay ? '0 8px 20px rgba(31,60,136,0.18)' : 'none',
            }}>
            <div className="d-flex align-items-center gap-2 px-2 py-1" style={{ flex: '1 1 0', minWidth: 0 }}>
                <FaGripVertical size={12} style={{ color: '#9fb3d4', flexShrink: 0 }} />
                {inter && (
                    <span className="d-flex align-items-center gap-1 px-2 py-1 fw-semibold text-uppercase text-nowrap"
                        style={{ ...BADGE_ITEM, color: BRAND_BLUE, background: '#e4eeff' }}
                        title="Internacional: tarifa de ida e volta. A cotação já vem pareada, e ida e volta andam juntas.">
                        <FaGlobeAmericas size={10} /> Inter
                    </span>
                )}
                {importada && (
                    <span className="d-flex align-items-center gap-1 px-2 py-1 fw-semibold text-uppercase text-nowrap"
                        style={{ ...BADGE_ITEM, color: '#15803d', background: '#e6f4ea' }}
                        title="Importada: está nas opções do evento. Anda como uma linha só, e não se agrupa nem se exclui aqui.">
                        <FaCheckCircle size={10} /> No evento
                    </span>
                )}
                {lista(ida, resumoIda)}
            </div>
            <span style={{ width: 1, background: BRAND_BORDER, flexShrink: 0 }} />
            <div className="d-flex align-items-center gap-2 px-2 py-1" style={{ flex: '1 1 0', minWidth: 0 }}>
                {lista(ret, resumoRet)}
                {acoes && <span className="ms-auto d-flex align-items-center" style={{ flexShrink: 0 }} onPointerDown={e => e.stopPropagation()}>{acoes}</span>}
            </div>
        </div>
    );
}

// Pareamento IDA × RETORNO: duas colunas dnd-kit independentes, alinhadas por
// índice (linha i da IDA ↔ linha i do RET). Dentro de cada coluna, o (...) →
// Agrupar empilha trechos num itinerário. Abrir SEPARA o que estava juntado;
// Aplicar funde por lado (mesclarLado: '\n' + soma) e zipa com fundir.
// `importadas`: os ids das opções que estão no evento (`aprovacao.opcoesPortadas`).
export default function AjustarOpcoesModal({ aberto, opcoes, importadas = null, onAplicar, onFechar }) {
    // Cada coluna é uma lista de células { id, itens } (itens = trechos de linha única).
    const [idaCells, setIdaCells] = useState([]);
    const [retCells, setRetCells] = useState([]);
    const [ativo, setAtivo] = useState(null); // item arrastado (overlay): { tipo, lado, cell?/trecho? }
    // Grupo ATIVO (agrupando): { lado, id } | null — único nas 2 colunas. No par internacional,
    // { lado: 'ida', id, ret }: a edição abre nas duas metades da mesma linha.
    const [agrupando, setAgrupando] = useState(null);
    // Toque: segura 50ms para arrastar (touch-action em custom.css). Mouse: 5px, como antes —
    // com delay, o dnd-kit cancelaria o arraste rápido que passa de 5px antes da espera.
    const sensores = useSensors(useSensor(PointerSensor, {
        activationConstraint: window.matchMedia('(pointer: coarse)').matches
            ? { delay: 50, tolerance: 5 }
            : { distance: 5 },
    }));
    // A soltura que junta uma célula da OUTRA coluna num grupo anima até o grupo. A do dnd-kit leva o
    // overlay ao nó do item arrastado, e ele fica no lugar de onde saiu, vazio e com o mesmo id: a
    // junção parecia voltar para a coluna de origem. `destinoDaSoltura` é a célula do grupo, marcada no
    // `aoSoltar` só quando essa junção acontece; sem ela, a animação é a de sempre.
    const destinoDaSoltura = useRef(null);
    const animacaoDaSoltura = useMemo(() => ({
        ...defaultDropAnimation,
        keyframes: ({ transform, dragOverlay, droppableContainers }) => {
            const no = destinoDaSoltura.current != null ? droppableContainers.get(destinoDaSoltura.current)?.node.current : null;
            if (!no) return defaultDropAnimation.keyframes({ transform });
            const r = no.getBoundingClientRect();
            const final = {
                ...transform.initial,
                x: transform.initial.x - (dragOverlay.rect.left - r.left),
                y: transform.initial.y - (dragOverlay.rect.top - r.top),
            };
            return [
                { transform: CSS.Transform.toString(transform.initial), opacity: 1 },
                { transform: CSS.Transform.toString(final), opacity: 0 },
            ];
        },
    }), []);

    useEffect(() => {
        if (!aberto) return;
        const { idaItems, retItems } = decompor(opcoes);
        // Par internacional: só com as duas metades. Internacional de um lado só é trecho solto — a
        // conexão que o parser não encadeou, por exemplo — e fica livre para entrar num par. A marca
        // mora na CÉLULA: ela acompanha a linha quando o par troca de lugar e não passa para trecho
        // nenhum que saia dele.
        const par = (i) => opcoes[i]?.nacionalidade === 'internacional' && !!idaItems[i] && !!retItems[i];
        // A importada trava a linha do mesmo jeito, mas não vira internacional: a marca é outra
        // (`importada`), e o `par` segue só dizendo a nacionalidade no Aplicar.
        const importada = (i) => !!opcoes[i]?.id && !!importadas?.has(String(opcoes[i].id));
        const ida = idaItems.map((item, i) => ({ id: `ida-${i}`, par: par(i), importada: importada(i), itens: comUid(item ? separarLado(item, 'ida') : [], `ida-${i}`) }));
        const ret = retItems.map((item, i) => ({ id: `ret-${i}`, par: par(i), importada: importada(i), itens: comUid(item ? separarLado(item, 'ret') : [], `ret-${i}`) }));
        const [ni, nr] = normalizarPares(ida, ret);
        setIdaCells(ni);
        setRetCells(nr);
        setAgrupando(null); // reabriu → nenhum grupo ativo
    }, [aberto, opcoes, importadas]);

    // Mantém a invariante (1 par de whitespace no fim) após qualquer operação — inclusive
    // as de coluna (excluir/desagrupar). Guarda contra: (1) estado inicial vazio (senão
    // sobrescreve o conteúdo que o effect de abertura ainda vai setar — clobber por closure
    // velha) e (2) loop (jaNormalizado).
    useEffect(() => {
        if (!aberto || idaCells.length === 0 || jaNormalizado(idaCells, retCells)) return;
        const [ni, nr] = normalizarPares(idaCells, retCells);
        setIdaCells(ni);
        setRetCells(nr);
    }, [idaCells, retCells, aberto]);

    // Par internacional: a tarifa é de ida e volta, e a cotação já chega pareada e com as
    // conexões encadeadas pelo parser. As duas metades não se separam: fechado, o par é um card
    // só atravessando as colunas e se move como LINHA; aberto (Agrupar), cada metade se edita na
    // sua coluna, sem sair dela. A opção que está no evento trava do mesmo jeito, e não abre.
    const travados = useMemo(() => new Set(
        [...idaCells, ...retCells].filter(c => c.par || c.importada).map(c => String(c.id))
    ), [idaCells, retCells]);

    if (!aberto) return null;

    const cellsDe = (l) => (l === 'ida' ? idaCells : retCells);
    const resumoDe = (l) => (it) => resumoTrecho(it, l);

    // Grupo ativo ÚNICO nas duas colunas: agrupar numa desativa a outra (estado único);
    // reclicar a mesma desativa. O par internacional é a exceção que confirma: abre as DUAS
    // metades da linha, e o ✓ de qualquer uma fecha as duas. A opção que está no evento não abre.
    const agrupar = (lado, id) => {
        if (cellsDe(lado).find(c => String(c.id) === String(id))?.importada) {
            toast.warning(AVISO_IMPORTADA);
            return;
        }
        setAgrupando(prev => {
            const mesmo = prev && ((prev.lado === lado && String(prev.id) === String(id))
                || (lado === 'ret' && prev.ret != null && String(prev.ret) === String(id)));
            if (mesmo) return null;
            const i = cellsDe(lado).findIndex(c => String(c.id) === String(id));
            if (i >= 0 && cellsDe(lado)[i].par) return { lado: 'ida', id: idaCells[i].id, ret: retCells[i].id };
            return { lado, id };
        });
    };
    // Reorder com grupo ativo é NORMAL — a única exceção é o próprio grupo como alvo (soltar
    // nele JUNTA, não troca), tratada pela estratégia/drop da ColunaArrastavel via agrupandoId.

    // Colisão por tipo do arrasto (antes vivia em ColunaArrastavel, agora no contexto
    // único): arrastando CÉLULA ignora sub-chips; arrastando SUB-CHIP ignora slots.
    const colisao = (params) => {
        const tipoAtivo = params.active?.data?.current?.tipo;
        const ignorar = tipoAtivo === 'subchip' ? 'nest' : 'subchip';
        // O par internacional troca a LINHA inteira: mira só nas células da coluna da IDA (é a
        // metade que representa cada linha). Os demais não miram nele — soltar uma célula avulsa
        // sobre o par o desmancharia.
        const ativoPar = !!params.active?.data?.current?.par;
        const containers = params.droppableContainers.filter(c => {
            const d = c.data?.current || {};
            if (ativoPar) return d.tipo === 'cell' && d.lado === 'ida';
            return d.tipo !== ignorar && !d.par;
        });
        const args = { ...params, droppableContainers: containers };
        // O par mira a LINHA do cursor, em qualquer coluna: as linhas são as mesmas nas duas, e o
        // card pode ter sido pego pela metade da volta, que fica sobre a coluna do RETORNO — longe
        // de toda célula da IDA. Então só a altura do cursor conta: dentro da caixa de uma célula da
        // IDA, é ela; no vão entre duas, a de borda mais perto. É o mesmo gatilho dos arrastes
        // comuns (o cursor entrando na célula); sem isto, pego pela direita, o par caía inteiro no
        // `closestCorners`, que mede a caixa arrastada e não o cursor, e trocava em outro ponto.
        const y = params.pointerCoordinates?.y;
        if (ativoPar && y != null) {
            let alvo = null;
            let distancia = Infinity;
            containers.forEach(c => {
                const r = params.droppableRects.get(c.id);
                if (!r) return;
                const d = y < r.top ? r.top - y : (y > r.bottom ? y - r.bottom : 0);
                if (d < distancia) { distancia = d; alvo = c; }
            });
            if (alvo) return [{ id: alvo.id, data: { droppableContainer: alvo, value: distancia } }];
        }
        // O ALVO é a caixa em que o cursor está, e só quando ele está fora de todas é que vale a
        // mais próxima. As caixas aqui têm alturas muito diferentes — a célula em edição estica a
        // linha, e a da outra coluna estica junto —, e o `closestCorners` sozinho media até os
        // CANTOS: parado no meio de uma linha alta, o cursor ficava mais perto dos cantos de uma
        // célula vizinha pequena do que dos da própria, e o alvo pulava. Caixa aninhada (o slot
        // "solte aqui para juntar") ganha da célula que a contém, que é o que se espera dela.
        const dentro = pointerWithin(args);
        return dentro.length ? dentro : closestCorners(args);
    };

    const aoIniciar = (e) => {
        destinoDaSoltura.current = null;
        const d = e.active.data?.current || {};
        const cells = cellsDe(d.lado);
        if (d.tipo === 'subchip') {
            const cell = cells.find(c => String(c.id) === String(d.cellId));
            setAtivo({ tipo: 'subchip', lado: d.lado, trecho: cell && cell.itens.find(t => t._uid === e.active.id) });
        } else {
            const i = cells.findIndex(c => c.id === e.active.id);
            // Do par vem também a metade da volta, para o overlay desenhar o card inteiro.
            setAtivo({ tipo: 'cell', lado: d.lado, cell: cells[i], par: !!d.par, ret: d.par ? retCells[i] : null });
        }
    };

    // Cruzamento IDA↔RETORNO no DROP (não no hover). Duas operações cruzam a coluna:
    //  • CÉLULA inteira → vai p/ um slot VAZIO da outra coluna (troca com a vazia — mover a
    //    célula, não só o conteúdo, faz a animação cair no lugar) ou JUNTA num grupo ativo
    //    dela (slot "solte aqui para juntar"). Sobre um slot OCUPADO, abre uma LINHA NOVA no lugar
    //    dele, com a célula do lado de lá e o outro vazio: sem descer até o par vazio do fim.
    //  • SUB-CHIP (um trecho de dentro de um grupo) → é EXTRAÍDO do grupo e largado na outra
    //    coluna: slot vazio vira célula própria; ocupado, abre a linha nova do mesmo jeito.
    // O trecho é remapeado ida↔ret; a origem some; nenhuma outra opção é tocada. Depois
    // normaliza (remove pares vazios extras, mantém 1 no fim). Mesma-coluna → ColunaArrastavel.
    // Excluir o par tira a OPÇÃO inteira: as duas metades esvaziam juntas, e a linha vazia some na
    // normalização. Como o Excluir das outras células, só vale depois do Aplicar e do Salvar.
    const excluirPar = (idIda) => {
        const i = idaCells.findIndex(c => String(c.id) === String(idIda));
        if (i < 0) return;
        if (idaCells[i].importada) {
            toast.warning(AVISO_IMPORTADA);
            return;
        }
        const esvazia = (lista) => lista.map((c, k) => (k === i ? { ...c, itens: [] } : c));
        const [ni, nr] = normalizarPares(esvazia(idaCells), esvazia(retCells));
        setIdaCells(ni);
        setRetCells(nr);
    };

    const aoSoltar = (e) => {
        setAtivo(null);
        const { active, over } = e;
        if (!over) return;
        const da = active.data?.current || {};
        const al = over.data?.current || {};

        // Mapeia (fonte,destino) → (ida,ret), normaliza e aplica de uma vez.
        const aplicarPar = (ladoDe, novoFonte, novoDestino) => {
            const [ni, nr] = normalizarPares(
                ladoDe === 'ida' ? novoFonte : novoDestino,
                ladoDe === 'ida' ? novoDestino : novoFonte,
            );
            setIdaCells(ni);
            setRetCells(nr);
        };

        // Par internacional: troca a LINHA inteira com a do alvo, nas duas colunas ao mesmo tempo
        // — a linha de lá leva a volta dela junto, então nenhum par, internacional ou não, se
        // desfaz. O `colisao` já garante que o alvo é uma célula da IDA.
        if (da.par) {
            if (String(active.id) === String(over.id)) return;
            const de = idaCells.findIndex(c => c.id === active.id);
            const para = idaCells.findIndex(c => String(c.id) === String(over.id));
            if (de < 0 || para < 0) return;
            const troca = (lista) => {
                const next = [...lista];
                [next[de], next[para]] = [next[para], next[de]];
                return next;
            };
            const [ni, nr] = normalizarPares(troca(idaCells), troca(retCells));
            setIdaCells(ni);
            setRetCells(nr);
            return;
        }

        // SUB-CHIP cruzando a coluna: tira o trecho do grupo de origem e larga na outra coluna,
        // num slot vazio ou numa linha nova no lugar do ocupado (vira trecho avulso). NUNCA agrupa
        // numa célula ocupada — juntar é só pelo fluxo de grupo ativo ("solte aqui para juntar"), e
        // o grupo ativo é a própria origem do sub-chip, nunca a outra coluna.
        if (da.tipo === 'subchip') {
            const ladoDe = da.lado;
            const ladoPara = al.lado;
            if (!ladoPara || ladoPara === ladoDe) return; // mesma coluna → ColunaArrastavel trata
            if (al.tipo !== 'cell') return;                // só sobre uma célula/slot
            const fonte = cellsDe(ladoDe);
            const ci = fonte.findIndex(c => String(c.id) === String(da.cellId));
            if (ci < 0 || fonte[ci].itens.length <= 1) return; // só extrai de um GRUPO (>1 trecho)
            const idx = fonte[ci].itens.findIndex(t => t._uid === active.id);
            if (idx < 0) return;
            const destino = cellsDe(ladoPara);
            const ai = destino.findIndex(c => String(c.id) === String(over.id));
            if (ai < 0) return;
            const trecho = remapLado(fonte[ci].itens[idx], ladoDe, ladoPara);
            const semTrecho = fonte.map((c, i) => (i === ci ? { ...c, itens: c.itens.filter((_, k) => k !== idx) } : c));
            if (destino[ai].itens.length > 0) {
                // Ocupado: linha nova no lugar dele, com o trecho do lado de lá e o outro vazio.
                aplicarPar(ladoDe, inserir(semTrecho, ai, novoVazio(ladoDe)), inserir(destino, ai, { ...novoVazio(ladoPara), itens: [trecho] }));
                return;
            }
            aplicarPar(ladoDe, semTrecho, destino.map((c, i) => (i === ai ? { ...c, itens: [trecho] } : c)));
            return;
        }

        if (da.tipo !== 'cell') return; // o resto cruza coluna só arrastando uma CÉLULA
        const ladoDe = idaCells.some(c => String(c.id) === String(active.id)) ? 'ida' : 'ret';
        const di = cellsDe(ladoDe).findIndex(c => String(c.id) === String(active.id));
        if (di < 0) return;
        const movidos = cellsDe(ladoDe)[di].itens.map(t => remapLado(t, ladoDe, al.lado));

        // 1) JUNTAR num grupo ativo da OUTRA coluna — pelo slot "solte aqui para juntar"
        // (tipo 'nest') OU soltando direto sobre a PRÓPRIA célula ativa (tipo 'cell').
        const juntarEm =
            (al.tipo === 'nest' && al.lado && al.lado !== ladoDe) ? { lado: al.lado, cellId: al.alvoId }
            : (al.tipo === 'cell' && al.lado && al.lado !== ladoDe
                && agrupando && agrupando.lado === al.lado && String(agrupando.id) === String(over.id))
                ? { lado: al.lado, cellId: over.id } : null;
        if (juntarEm) {
            const fonte = cellsDe(ladoDe), destino = cellsDe(juntarEm.lado);
            const ai = destino.findIndex(c => String(c.id) === String(juntarEm.cellId));
            if (ai < 0) return;
            destinoDaSoltura.current = juntarEm.cellId;
            aplicarPar(ladoDe,
                fonte.map((c, i) => (i === di ? { ...c, itens: [] } : c)),
                destino.map((c, i) => (i === ai ? { ...c, itens: [...c.itens, ...movidos] } : c)),
            );
            return;
        }

        // 2) MOVER p/ um slot VAZIO da outra coluna (move a CÉLULA → animação cai certo).
        if (al.tipo !== 'cell') return;
        const ladoPara = idaCells.some(c => String(c.id) === String(over.id)) ? 'ida' : 'ret';
        if (ladoDe === ladoPara) return; // mesma coluna → a ColunaArrastavel resolve
        const fonte = cellsDe(ladoDe), destino = cellsDe(ladoPara);
        const ai = destino.findIndex(c => String(c.id) === String(over.id));
        if (ai < 0) return;
        const ativoCel = { ...fonte[di], itens: movidos };
        if (destino[ai].itens.length > 0) {
            // Ocupado: linha nova no lugar do alvo, com a célula do lado de lá e o outro vazio. A
            // origem fica vazia, e a normalização tira a linha dela se sobrar sem nada.
            aplicarPar(ladoDe,
                inserir(fonte.map((c, i) => (i === di ? novoVazio(ladoDe) : c)), ai, novoVazio(ladoDe)),
                inserir(destino, ai, ativoCel),
            );
        } else {
            aplicarPar(ladoDe,
                fonte.map((c, i) => (i === di ? destino[ai] : c)),  // origem recebe a célula vazia
                destino.map((c, i) => (i === ai ? ativoCel : c)),   // destino recebe a célula ativa
            );
        }
        // Se a célula movida era o grupo ATIVO, migra o "grupo ativo" p/ a nova coluna
        // (o id é preservado no move) → segue agrupando do outro lado, sem perder o estado.
        if (agrupando && agrupando.lado === ladoDe && String(agrupando.id) === String(active.id)) {
            setAgrupando({ lado: ladoPara, id: active.id });
        }
    };

    const total = Math.max(idaCells.length, retCells.length);
    // Altura por linha = máx. das duas colunas → IDA e RET (e o gutter) alinham. A célula
    // ATIVA (agrupando) mede pelo modo edição (card + slot juntar) → a CÉLULA da linha oposta
    // estica junto; o item visível dela mantém o tamanho (centralizado). Muda só no clique de
    // Agrupar/✓ — nunca durante o arrasto (preview não altera altura; overflow clipado abaixo).
    const alturaDe = (cell, lado) => {
        const n = cell ? cell.itens.length : 0;
        const ativa = !!(n > 0 && agrupando && (
            (agrupando.lado === lado && String(agrupando.id) === String(cell.id))
            // A metade do RETORNO de um par em edição também é ativa.
            || (lado === 'ret' && agrupando.ret != null && String(agrupando.ret) === String(cell.id))));
        return ativa ? alturaAtiva(n) : alturaCelula(n);
    };
    const alturas = Array.from({ length: total }, (_, i) => Math.max(
        alturaDe(idaCells[i], 'ida'),
        alturaDe(retCells[i], 'ret'),
    ));

    // A metade da IDA desenha o par inteiro (largura das duas colunas mais o gutter entre elas);
    // a do RETORNO vira espaçador, só para a linha manter a altura nas duas colunas. `acoes` é o
    // (⋯) do par, que a célula monta.
    const renderTravado = (cell, i, lado, acoes) => {
        if (lado === 'ret') return null; // a altura da linha é do invólucro da célula
        return <ParTravado ida={cell.itens} ret={(retCells[i] || {}).itens || []} acoes={acoes}
            inter={!!cell.par} importada={!!cell.importada}
            resumoIda={resumoDe('ida')} resumoRet={resumoDe('ret')} />;
    };

    const aplicar = () => onAplicar(fundir(
        idaCells.map(c => (c.itens.length ? mesclarLado(c.itens, 'ida') : null)),
        retCells.map(c => (c.itens.length ? mesclarLado(c.itens, 'ret') : null)),
        // A nacionalidade sai da LINHA: par é internacional; trecho que saiu de um par deixa de ser.
        idaCells.map((c, i) => !!(c.par && retCells[i]?.par)),
    ));

    // Sem fechar no clique fora, como antes: o ajuste é rascunho, e um arrasto solto fora do card não pode
    // descartá-lo.
    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaExchangeAlt size={16} /></span>
                        <h3 className="fin-pp__modal-title">Ajustar malha</h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar}><FaTimes size={16} /></button>
                    </div>

                    <div className="modal-body">
                        <p style={{ color: '#5f6f8a' }}>
                            Arraste os itens em cada coluna para alinhar cada IDA com seu RETORNO. Use o menu
                            <strong> (⋯) → Agrupar</strong> de um trecho para empilhar outros trechos num itinerário.
                            Soltar um trecho sobre uma linha ocupada do outro lado abre uma linha nova ali.
                            {idaCells.some(c => c.par) &&' As opções internacionais vêm pareadas da cotação e se movem como uma linha só: a ida e a volta delas não se separam.'}
                            {idaCells.some(c => c.importada) && ' As que estão no evento também se movem como uma linha só, e não se agrupam nem se excluem aqui.'}
                        </p>

                        {/* Cabeçalho das colunas */}
                        <div className="d-flex gap-2 mb-2 fw-bold text-uppercase" style={{ color: BRAND_NAVY, fontSize: 12, letterSpacing: '0.06em' }}>
                            <div style={{ width: 28, flexShrink: 0 }}></div>
                            <div className="d-flex align-items-center gap-2" style={{ flex: '1 1 0', minWidth: 0 }}>
                                <FaPlaneDeparture size={12} style={{ color: BRAND_BLUE }} /> IDA
                            </div>
                            <div className="d-flex align-items-center gap-2" style={{ flex: '1 1 0', minWidth: 0 }}>
                                <FaPlaneArrival size={12} style={{ color: BRAND_BLUE }} /> RETORNO
                            </div>
                        </div>

                        {/* Gutter de números + duas colunas alinhadas por índice, sob um
                            ÚNICO DndContext → permite arrastar um trecho entre IDA e RETORNO. */}
                        <DndContext sensors={sensores} collisionDetection={colisao}
                            onDragStart={aoIniciar} onDragEnd={aoSoltar} onDragCancel={() => setAtivo(null)}>
                            {/* overflow escondido SÓ durante o arrasto: o preview (translate) de um
                                item alto além da última linha crescia a área de scroll do modal — a
                                barra aparecia/sumia e o scroll pulava. Clipar o excesso transitório
                                congela a altura; o DragOverlay é position:fixed e não é afetado. */}
                            <div className="d-flex gap-2 align-items-start" style={{ overflow: ativo ? 'hidden' : 'visible' }}>
                                {/* Gutter de números — sempre visível: as linhas acompanham o card em
                                    edição (alturaAtiva), então a numeração se mantém alinhada. */}
                                <div className="d-flex flex-column gap-2" style={{ width: 28, flexShrink: 0 }}>
                                    {Array.from({ length: total }).map((_, i) => (
                                        <div key={i} className="d-flex align-items-center justify-content-center fw-bold"
                                            style={{ minHeight: alturas[i], color: '#7b8ba7' }}>
                                            {i + 1}
                                        </div>
                                    ))}
                                </div>
                                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <ColunaArrastavel cells={idaCells} setCells={setIdaCells} lado="ida"
                                        arrastandoSub={ativo?.tipo === 'subchip'}
                                        agrupandoId={agrupando && agrupando.lado === 'ida' ? agrupando.id : null}
                                        onAgrupar={(id) => agrupar('ida', id)}
                                        travados={travados} renderTravado={renderTravado} onExcluirPar={excluirPar}
                                        resumo={resumoDe('ida')} alturas={alturas} rotuloGrupo="Conexão" />
                                </div>
                                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <ColunaArrastavel cells={retCells} setCells={setRetCells} lado="ret"
                                        arrastandoSub={ativo?.tipo === 'subchip'}
                                        agrupandoId={agrupando ? (agrupando.lado === 'ret' ? agrupando.id : (agrupando.ret ?? null)) : null}
                                        idsIda={idaCells.map(c => c.id)}
                                        onAgrupar={(id) => agrupar('ret', id)}
                                        travados={travados} renderTravado={renderTravado}
                                        resumo={resumoDe('ret')} alturas={alturas} rotuloGrupo="Conexão" />
                                </div>
                            </div>
                            <DragOverlay dropAnimation={animacaoDaSoltura}>
                                {ativo && (ativo.par
                                    ? <ParTravado ida={ativo.cell?.itens || []} ret={ativo.ret?.itens || []} overlay
                                        inter={!!ativo.cell?.par} importada={!!ativo.cell?.importada}
                                        resumoIda={resumoDe('ida')} resumoRet={resumoDe('ret')} />
                                    : <OverlayConteudo ativo={ativo} resumo={resumoDe(ativo.lado)} />)}
                            </DragOverlay>
                        </DndContext>
                    </div>

                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={onFechar}>Cancelar</button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={aplicar}>Aplicar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
